import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Text,
  Button,
  Flex,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useToast,
  Image,
  Input,
  SimpleGrid,
  RadioGroup,
  Radio,
  IconButton,
  Spinner,
  HStack,
  Icon,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Checkbox,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  VStack,
  useDisclosure
} from '@chakra-ui/react';
import { Link, useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { FaCreditCard, FaApple, FaBitcoin, FaTimes, FaGoogle } from 'react-icons/fa';
import { FiBriefcase } from 'react-icons/fi';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../hooks/useAuth';
import { useSubscription } from '../../context/SubscriptionContext';
import axiosInstance from '../../services/axios';
import CustomSelect from '../../utils/CustomSelect';
import PaymentRequestButton from '../Payments/PaymentRequestButton';
import MerchantSwitchConfirmation from '../Dashboard/Merchant/MerchantSwitchConfirmation';

const PaymentMethodSelect = ({ selectedPaymentMethod, setSelectedPaymentMethod, applePaySupported, googlePaySupported, supportCheckLoading }) => (
  <FormControl overflowX="auto" w="100%" mt={4} backgroundColor="secondary.900" p={3}>
    <FormLabel fontSize="lg" fontWeight="bold">Payment Methods</FormLabel>
    <RadioGroup onChange={setSelectedPaymentMethod} value={selectedPaymentMethod}>
      <HStack spacing={8}>
        <Box display="flex" alignItems="center" cursor="pointer">
          <Radio value="card">
            <HStack>
              <Icon as={FaCreditCard} boxSize={8} />
              <Text>Card</Text>
            </HStack>
          </Radio>
        </Box>
        <Box display="flex" alignItems="center" cursor="pointer">
          <Radio value="applePay" isDisabled={!applePaySupported && !supportCheckLoading}>
            <HStack>
              <Icon as={FaApple} boxSize={8} color={!applePaySupported && !supportCheckLoading ? "gray.500" : "inherit"} />
              <Text color={!applePaySupported && !supportCheckLoading ? "gray.500" : "inherit"}>Apple Pay</Text>
            </HStack>
          </Radio>
        </Box>
        <Box display="flex" alignItems="center" cursor="pointer">
          <Radio value="googlePay" isDisabled={!googlePaySupported && !supportCheckLoading}>
            <HStack>
              <Icon as={FaGoogle} boxSize={8} color={!googlePaySupported && !supportCheckLoading ? "gray.500" : "inherit"} />
              <Text color={!googlePaySupported && !supportCheckLoading ? "gray.500" : "inherit"}>Google Pay</Text>
            </HStack>
          </Radio>
        </Box>
        <Box display="flex" alignItems="center" cursor="pointer">
          <Radio value="cryptoPay" isDisabled={true}>
            <HStack>
              <Icon as={FaBitcoin} boxSize={8} color="gray.500" />
              <Text color="gray.500">Crypto Pay</Text>
            </HStack>
          </Radio>
        </Box>
      </HStack>
    </RadioGroup> 
  </FormControl>
);

const Checkout = () => {
  const { cartItems, clearCart } = useCart();
  const { register, handleSubmit, formState: { errors }, control } = useForm();
  const navigate = useNavigate();
  const toast = useToast();
  const stripe = useStripe();
  const elements = useElements();
  const auth = useAuth();
  const { user, isAuthenticated } = auth;
  const { getPaymentMethods, createPaymentMethod, paymentMethods, validateBuyingCoupon, createPaymentIntent, createPaymentIntentWithoutSaving } = useSubscription();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [switching, setSwitching] = useState(false);
  const cancelRef = useRef();

  // Check if user is viewing in artist context (either switched from merchant or logged in as linked artist)
  // This matches the same logic used in the profile menu
  const isLinkedArtist = React.useMemo(() => {
    if (!isAuthenticated) {
      return false;
    }

    // Check if merchant has switched to artist context
    if (auth.isSwitchedContext) {
      return true;
    }

    // Check if logged in as a linked artist (artist created by merchant)
    if (user?.account_type === 'artist') {
      return !!(
        user?.merchant_id ||
        user?.account?.merchant_id ||
        user?.account?.profile?.merchant_id
      );
    }

    return false;
  }, [isAuthenticated, auth.isSwitchedContext, user]);

  // Calculate subtotal from cart items
  const calculateSubtotal = useCallback(() => {
    return cartItems.reduce((total, item) => total + (item.price_per_share * item.quantity), 0);
  }, [cartItems]);

  // Initial subtotal (displayed in dollars)
  const [subtotal, setSubtotal] = useState(calculateSubtotal());
  // Processing fee is 6.5% of subtotal plus $3.50
  const [processingFee, setProcessingFee] = useState(subtotal * 0.065 + 3.50);
  // Final total is subtotal + processing fee (for display purposes only)
  const [finalTotal, setFinalTotal] = useState(subtotal + subtotal * 0.065 + 3.50);

  // Update calculations when cart items change
  useEffect(() => {
    const newSubtotal = calculateSubtotal();
    const newProcessingFee = newSubtotal * 0.065 + 3.50;
    setSubtotal(newSubtotal);
    setProcessingFee(newProcessingFee);
    setFinalTotal(newSubtotal + newProcessingFee);
  }, [calculateSubtotal]);

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('card');
  const [paymentMethodSelected, setPaymentMethodSelected] = useState('');
  const [loading, setLoading] = useState(false);
  const [couponLoading, setCouponLoading] = useState(false);
  const [discount, setDiscount] = useState(null);
  const [discountCode, setDiscountCode] = useState('');
  const [showDiscount, setShowDiscount] = useState(false);
  // Toggle to save payment method for future use
  const [savePaymentMethod, setSavePaymentMethod] = useState(true);
  // Payment method support detection
  const [applePaySupported, setApplePaySupported] = useState(false);
  const [googlePaySupported, setGooglePaySupported] = useState(false);
  const [supportCheckLoading, setSupportCheckLoading] = useState(false);

  // Function to check if Apple Pay and Google Pay are supported
  const checkPaymentMethodSupport = useCallback(async () => {
    if (!stripe) return;

    setSupportCheckLoading(true);
    try {
      const paymentRequest = stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        total: {
          label: 'Test',
          amount: 100, // $1.00 for testing
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      const canMakePayment = await paymentRequest.canMakePayment();
      if (canMakePayment) {
        // Check which specific methods are available
        setApplePaySupported(canMakePayment.applePay || false);
        setGooglePaySupported(canMakePayment.googlePay || false);
      } else {
        setApplePaySupported(false);
        setGooglePaySupported(false);
      }
    } catch (error) {
      console.error('Error checking payment method support:', error);
      setApplePaySupported(false);
      setGooglePaySupported(false);
    } finally {
      setSupportCheckLoading(false);
    }
  }, [stripe]);

  // Check support when stripe is loaded
  useEffect(() => {
    if (stripe) {
      checkPaymentMethodSupport();
    }
  }, [stripe, checkPaymentMethodSupport]);

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        await getPaymentMethods();
      } catch (error) {
        toast({
          title: 'Error fetching payment methods',
          description: error.message || 'Please try again later.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    };

    fetchPaymentMethods();
  }, [getPaymentMethods, toast]);

  const COUNTRIES = [
    { name: "United States", code: "US" },
    { name: "Canada", code: "CA" },
    { name: "United Kingdom", code: "GB" },
    { name: "Australia", code: "AU" },
    { name: "Germany", code: "DE" },
    { name: "France", code: "FR" },
    { name: "India", code: "IN" },
    { name: "Japan", code: "JP" },
    { name: "Brazil", code: "BR" },
    { name: "Mexico", code: "MX" },
    { name: "South Africa", code: "ZA" },
    { name: "Singapore", code: "SG" },
  ];

  const CARD_OPTIONS = {
    style: {
      base: {
        fontSize: "16px",
        color: "#424770",
        fontFamily: "Poppins, sans-serif",
        "::placeholder": { color: "#aab7c4" },
        padding: "12px",
        backgroundColor: "transparent",
        borderRadius: "6px",
      },
      invalid: {
        color: "#ff4c4c",
        iconColor: "#ff4c4c",
      },
      complete: {
        color: "#4caf50",
      },
    },
    hidePostalCode: true,
    disableLink: true,
  };

  const handleApplyDiscount = async () => {
    setCouponLoading(true);
    try {
      const response = await validateBuyingCoupon(discountCode, subtotal);
      if (response?.is_valid) {
        // For display, assume discount adjusts the subtotal before fee calculation.
        const newSubtotal = response.final_price;
        const newProcessingFee = newSubtotal * 0.065 + 3.50;
        setSubtotal(newSubtotal);
        setProcessingFee(newProcessingFee);
        setFinalTotal(newSubtotal + newProcessingFee);
        setDiscount(response);
        setShowDiscount(true);
        toast({
          title: 'Discount applied',
          description: `Discount of ${response.percent_off ? `${response.percent_off}% off` : `$${response.amount_off}`} applied successfully!`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Invalid coupon',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setCouponLoading(false);
    }
  };

  // In these payment handlers we send the base subtotal (without processing fee)
  // because the backend will add the fee.
  const handlePaymentBySelected = async () => {
    setLoading(true);
    try {
      let paymentMethodId = paymentMethodSelected;
      if (!paymentMethodId) {
        toast({
          title: 'Error',
          description: 'Please select a payment method or pay using a new one',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      const amount = Math.round(subtotal * 100); // send base amount in cents with proper rounding
      const response = await createPaymentIntent(amount, 'usd', paymentMethodId);
      if (response) {
        console.log(response)
        await completeOrder(response.payment_intent.id);
      }
    } catch (error) {
      toast({
        title: 'Payment Error',
        description: error.message || 'Something went wrong. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (data) => {
    setLoading(true);
    try {
      let paymentMethodId = null;
      if (selectedPaymentMethod === 'card') {
        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
          toast({
            title: 'Error',
            description: 'Please enter valid card details',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        const billingDetails = {
          name: data.cardName,
          address: {
            line1: data.billingAddress,
            city: data.city,
            state: data.state,
            postal_code: data.postalCode,
            country: 'US',
          },
        };
        paymentMethodId = await createPaymentMethod(cardElement, stripe, billingDetails);
      } else if (selectedPaymentMethod === 'applePay' || selectedPaymentMethod === 'googlePay') {
        toast({
          title: 'Use the Payment Button Above',
          description: 'Please use the Apple Pay or Google Pay button above instead of the radio selection.',
          status: 'info',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      if (!paymentMethodId) {
        toast({
          title: 'Error',
          description: 'Please select a payment method or pay using a new one',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const amount = Math.round(subtotal * 100); // send base amount in cents with proper rounding
      let response = null;
      if (savePaymentMethod) {
        response = await createPaymentIntent(amount, 'usd', paymentMethodId);

      } else {
        response = await createPaymentIntentWithoutSaving(amount, 'usd', paymentMethodId);


      }
      if (response) {
        console.log(response)
        await completeOrder(response.payment_intent.id);
      }
    } catch (error) {
      toast({
        title: 'Payment Error',
        description: error.message || 'Something went wrong. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchToMerchant = async () => {
    setSwitching(true);
    try {
      await auth.switchBackToMerchant();

      toast({
        title: 'Switched to Merchant View',
        description: 'You can now complete your purchase',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onClose();
    } catch (error) {
      console.error('Error switching to merchant:', error);
      toast({
        title: 'Switch Failed',
        description: 'Failed to switch to merchant view',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSwitching(false);
    }
  };

  const completeOrder = async (intent_id) => {
    try {
      const totalShares = cartItems.reduce((sum, item) => sum + item.quantity, 0);

      // Make sure each cart item has a seller_id
      const cartItemsWithSeller = cartItems.map(item => {
        // If item already has seller_id, use it; otherwise, use the track's publisher id
        return {
          ...item,
          seller_id: item.seller_id || item.publisher.id
        };
      });

      await axiosInstance.post('/music/complete_order/', {
        cart_items: { items: cartItemsWithSeller },
        // For order completion, you may want to show the final total (display only)
        total_price: finalTotal,
        payment_method: selectedPaymentMethod === 'applePay' || selectedPaymentMethod === 'googlePay' ? 'wallet' : selectedPaymentMethod,
        buyer_id: user.user_id,
        total_shares: totalShares,
        transaction_type: 'shares',
        payment_intent_id: intent_id,
        processing_fee: processingFee,
      });
      clearCart();
      toast({
        title: 'Order placed successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      navigate('/order-confirmation');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to complete the order. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Redirect linked artists or show warning
  useEffect(() => {
    if (isLinkedArtist) {
      onOpen();
    }
  }, [isLinkedArtist, onOpen]);

  // If linked artist, show blocked message
  if (isLinkedArtist) {
    return (
      <Box p={8} maxW="800px" mx="auto">
        <Alert
          status="warning"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          minHeight="300px"
          borderRadius="md"
          bg="orange.100"
          color="orange.900"
        >
          <Icon as={FiBriefcase} boxSize={12} color="orange.500" mb={4} />
          <AlertTitle fontSize="2xl" mb={2}>Linked Artist Account</AlertTitle>
          <AlertDescription maxWidth="md" fontSize="md" mb={6}>
            Your account is managed by a merchant. Switch to your merchant account to complete purchases.
          </AlertDescription>
          <HStack spacing={4}>
            <Button
              colorScheme="orange"
              leftIcon={<Icon as={FiBriefcase} />}
              onClick={onOpen}
            >
              Switch to Merchant
            </Button>
            <Button
              variant="outline"
              colorScheme="orange"
              onClick={() => navigate('/cart')}
            >
              Back to Cart
            </Button>
          </HStack>
        </Alert>

        {/* Merchant Switch Confirmation Dialog */}
        <MerchantSwitchConfirmation
          isOpen={isOpen}
          onClose={() => {
            onClose();
            navigate('/cart');
          }}
          merchantName={user?.company_name}
          onConfirm={handleSwitchToMerchant}
          isLoading={switching}
          cancelRef={cancelRef}
        />
      </Box>
    );
  }

  return (
    <Box
      p={4}
      display="flex"
      flexDirection={{ base: 'column', md: 'row' }}
      justifyContent="space-around"
      alignItems="center"
      gap={5}
    >
      {/* Cart Summary */}
      <Box maxW="500px" flex={2} p={4} borderRadius="md" mb={{ base: 4, md: 0 }} bg="dark.500">
        <Text fontSize="2xl" mb={4}>Checkout</Text>
        {cartItems.map((item, index) => (
          <Flex key={index} align="center" gap={4} justify="space-between" p={3}>
            <Image
              src={item.image_url}
              alt={item.track_name}
              borderRadius="lg"
              width={{ base: "3rem", md: "4rem" }}
            />
            <Text as={Link} to={`/track/${item.track_id}`} w="50%" fontSize="sm" noOfLines={1} _hover={{ color: "dark.900" }}>
              {item.track_name}
            </Text>
            <Text>${item.price_per_share} x {item.quantity}</Text>
          </Flex>
        ))}

        {showDiscount && discount && (
          <Flex justifyContent="space-between" alignItems="center" mt={4} bg="secondary.100" color="primary.900" borderRadius={3}>
            <Text fontSize="md" p={4}>
              Applied Discount:{' '}
              <Text mx={2} color="green.300" as="span">
                {discount.percent_off
                  ? `${discount.percent_off}% off`
                  : `$${(discount.amount_off / 100).toFixed(2)} off`}
              </Text>
            </Text>
            <IconButton
              size="sm"
              color="red.500"
              icon={<FaTimes />}
              onClick={() => {
                setShowDiscount(false);
                setDiscountCode('');
                // Reset to original subtotal and recalculated fee
                const origSubtotal = calculateSubtotal();
                const origFee = origSubtotal * 0.065 + 3.50;
                setSubtotal(origSubtotal);
                setProcessingFee(origFee);
                setFinalTotal(origSubtotal + origFee);
                setDiscount(null);
              }}
            />
          </Flex>
        )}

        <Flex justifyContent="space-between" alignItems="center" mt={4} p={4} bg="secondary.100" borderRadius={3}>
          <Text fontSize="lg">Subtotal:</Text>
          <Text fontSize="lg">${subtotal.toFixed(2)}</Text>
        </Flex>
        <Flex justifyContent="space-between" alignItems="center" mt={2} p={4} bg="secondary.100" borderRadius={3}>
          <Text fontSize="lg">Processing Fee:</Text>
          <Text fontSize="lg">${processingFee.toFixed(2)}</Text>
        </Flex>
        <Flex justifyContent="space-between" alignItems="center" mt={2} p={4} bg="secondary.100" borderRadius={3}>
          <Text fontSize="lg">Total:</Text>
          <Text fontSize="lg">${finalTotal.toFixed(2)}</Text>
        </Flex>

        <FormControl mt={4}>
          <FormLabel>Discount Code</FormLabel>
          <Flex>
            <Input
              type="text"
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value)}
              placeholder="Enter discount code"
              variant="filled"
            />
            <Button onClick={handleApplyDiscount} ml={2}>
              {couponLoading ? <Spinner /> : "Apply"}
            </Button>
          </Flex>
        </FormControl>
      </Box>

      {/* Payment Section */}
      <Box flex={1} maxW="500px" bg="dark.500">
        <SimpleGrid columns={{ base: 1, md: 1 }} spacing={3}>
          {/* Existing Payment Methods */}
          {paymentMethods && paymentMethods.length > 0 && (
            <Box bg="secondary.100" borderRadius="lg" p={4}>
              <Text fontSize="lg" fontWeight="bold" mb={4}>
                Use saved payment method
              </Text>
              <FormControl>
                <CustomSelect
                  placeholder="Choose payment method"
                  value={paymentMethodSelected}
                  onChange={(val) => setPaymentMethodSelected(val)}
                  width="100%"
         
                >
                  {paymentMethods.map((method) => (
                    <option key={method.id} value={method.id}>
                      {method.card?.brand.toUpperCase()} ****{method.card?.last4}
                    </option>
                  ))}
                </CustomSelect>
              </FormControl>
              <Button
                mt={4}
                w="100%"
                isDisabled={!paymentMethodSelected}
                onClick={handlePaymentBySelected}
                isLoading={loading}
                bg="green.500"
              >
                Pay
              </Button>
            </Box>
          )}

          {/* Add New Payment Method */}
          <Accordion allowToggle p={4}>
            <AccordionItem borderTop="1px solid" borderColor="secondary.900">
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <Text fontSize="lg" fontWeight="bold">New payment method</Text>
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4}>
                <PaymentMethodSelect
                  selectedPaymentMethod={selectedPaymentMethod}
                  setSelectedPaymentMethod={setSelectedPaymentMethod}
                  applePaySupported={applePaySupported}
                  googlePaySupported={googlePaySupported}
                  supportCheckLoading={supportCheckLoading}
                />
                
                {/* Apple Pay and Google Pay Button */}
                <Box mt={4}>
                  <PaymentRequestButton
                    amount={Math.round(subtotal * 100)} // Convert to cents with proper rounding
                    currency="usd"
                    country="US"
                    label={`Cart Total - $${subtotal.toFixed(2)}`}
                    onPaymentSuccess={(paymentIntent, breakdown) => {
                      console.log('Payment successful:', paymentIntent, breakdown);
                      completeOrder(paymentIntent.id);
                    }}
                    onPaymentError={(error) => {
                      console.error('Payment error:', error);
                      toast({
                        title: "Payment Error",
                        description: error.message || "Payment failed. Please try again.",
                        status: "error",
                        duration: 5000,
                        isClosable: true,
                      });
                    }}
                  />
                </Box>

                {/* Divider between Apple Pay/Google Pay and Card payment */}
                <Box textAlign="center" my={4} position="relative">
                  <Text
                    bg="dark.500"
                    px={3}
                    color="gray.400"
                    fontSize="sm"
                    position="relative"
                    zIndex={1}
                  >
                    OR
                  </Text>
                  <Box
                    position="absolute"
                    top="50%"
                    left={0}
                    right={0}
                    height="1px"
                    bg="gray.600"
                    zIndex={0}
                  />
                </Box>

                {/* Support Status Messages */}
                {selectedPaymentMethod === 'applePay' && !applePaySupported && !supportCheckLoading && (
                  <Box mt={4} p={3} bg="red.100" borderRadius="md" border="1px solid" borderColor="red.300">
                    <Text color="red.700" fontSize="sm" fontWeight="bold">
                      ⚠️ Apple Pay is not supported
                    </Text>
                    <Text color="red.600" fontSize="xs" mt={1}>
                      Apple Pay requires Safari browser with saved payment methods on a supported device.
                    </Text>
                  </Box>
                )}

                {selectedPaymentMethod === 'googlePay' && !googlePaySupported && !supportCheckLoading && (
                  <Box mt={4} p={3} bg="red.100" borderRadius="md" border="1px solid" borderColor="red.300">
                    <Text color="red.700" fontSize="sm" fontWeight="bold">
                      ⚠️ Google Pay is not supported
                    </Text>
                    <Text color="red.600" fontSize="xs" mt={1}>
                      Google Pay requires Chrome browser with saved payment methods.
                    </Text>
                  </Box>
                )}

                {selectedPaymentMethod === 'cryptoPay' && (
                  <Box mt={4} p={3} bg="gray.100" borderRadius="md" border="1px solid" borderColor="gray.300">
                    <Text color="gray.700" fontSize="sm" fontWeight="bold">
                      🚧 Crypto Pay is not implemented
                    </Text>
                    <Text color="gray.600" fontSize="xs" mt={1}>
                      Cryptocurrency payments are coming soon.
                    </Text>
                  </Box>
                )}

                {supportCheckLoading && (selectedPaymentMethod === 'applePay' || selectedPaymentMethod === 'googlePay') && (
                  <Box mt={4} p={3} bg="blue.100" borderRadius="md" border="1px solid" borderColor="blue.300">
                    <Flex align="center">
                      <Spinner size="sm" color="blue.500" mr={2} />
                      <Text color="blue.700" fontSize="sm">
                        Checking payment method support...
                      </Text>
                    </Flex>
                  </Box>
                )}

                {selectedPaymentMethod === 'card' && (
                  <>
                    <FormControl mt={4} isInvalid={errors.cardName}>
                      <FormLabel fontSize={{ base: 'sm', md: 'md' }} color="primary.100">Name on Card</FormLabel>
                      <Input
                        type="text"
                        {...register("cardName", { required: "Name on card is required" })}
                        variant="filled"
                      />
                      <FormErrorMessage>{errors.cardName && errors.cardName.message}</FormErrorMessage>
                    </FormControl>
                    <SimpleGrid columns={2} spacing={4} mt={4}>
                      <FormControl isInvalid={errors.billingAddress}>
                        <FormLabel fontSize={{ base: 'sm', md: 'md' }} color="primary.100">
                          Billing Address
                        </FormLabel>
                        <Input
                          type="text"
                          {...register('billingAddress', { required: 'Address line 1 is required' })}
                          variant="filled"


                        />
                        <FormErrorMessage>{errors.billingAddress && errors.billingAddress.message}</FormErrorMessage>
                      </FormControl>
                      <FormControl isInvalid={errors.city}>
                        <FormLabel fontSize={{ base: 'sm', md: 'md' }} color="primary.100">City</FormLabel>
                        <Input
                          type="text"
                          {...register("city", { required: "City is required" })}
                          variant="filled"
                        />
                        <FormErrorMessage>{errors.city && errors.city.message}</FormErrorMessage>
                      </FormControl>
                      <FormControl isInvalid={errors.state}>
                        <FormLabel fontSize={{ base: 'sm', md: 'md' }} color="primary.100">State</FormLabel>
                        <Input
                          type="text"
                          {...register("state", { required: "State is required" })}
                          variant="filled"
                        />
                        <FormErrorMessage>{errors.state && errors.state.message}</FormErrorMessage>
                      </FormControl>
                      <FormControl isInvalid={errors.country}>
                        <FormLabel fontSize={{ base: 'sm', md: 'md' }} color="primary.100">
                          Country
                        </FormLabel>

                        <Controller
                          name="country"
                          control={control}
                          rules={{ required: 'Country is required' }}
                          render={({ field }) => (
                            <CustomSelect
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="Select a country"
                              width="100%"
                            >
                              {COUNTRIES.map((country) => (
                                <option key={country.code} value={country.code}>
                                  {country.name}
                                </option>
                              ))}
                            </CustomSelect>
                          )}
                        />

                        <FormErrorMessage>
                          {errors.country && errors.country.message}
                        </FormErrorMessage>
                      </FormControl>
                      <FormControl isInvalid={errors.postalCode}>
                        <FormLabel fontSize={{ base: 'sm', md: 'md' }} color="primary.100">
                          ZIP Code
                        </FormLabel>
                        <Input
                          type="text"
                          {...register('postalCode', { required: 'ZIP/Postal code is required' })}
                          variant="filled"
                          size="md"

                        />
                        <FormErrorMessage>{errors.postalCode && errors.postalCode.message}</FormErrorMessage>
                      </FormControl>
                    </SimpleGrid>
                    <FormControl mt={4}>
                      <FormLabel fontSize={{ base: 'sm', md: 'md' }}>Card Details</FormLabel>
                      <Box
                        p={4}
                        borderWidth="1px"
                        borderRadius="md"
                        bg="white"
                        _focus={{ borderColor: "primary.500", boxShadow: "0px 0px 8px rgba(72, 187, 120, 0.8)" }}
                      >
                        <CardElement options={CARD_OPTIONS} />
                      </Box>
                    </FormControl>
                  </>
                )}
                <FormControl mt={4}>
                  <Checkbox
                    isChecked={savePaymentMethod}
                    onChange={(e) => setSavePaymentMethod(e.target.checked)}
                  >
                    Save this payment method for future use
                  </Checkbox>
                </FormControl>
                <Button
                  mt={4}
                  w="100%"
                  onClick={handleSubmit(handlePayment)}
                  isLoading={loading}
                  bg="green.500"
                  isDisabled={
                    loading ||
                    (selectedPaymentMethod === 'applePay' && !applePaySupported) ||
                    (selectedPaymentMethod === 'googlePay' && !googlePaySupported) ||
                    selectedPaymentMethod === 'cryptoPay' ||
                    supportCheckLoading
                  }
                >
                  {savePaymentMethod ? 'Save and Pay' : 'Pay'}
                </Button>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </SimpleGrid>
      </Box>
    </Box>
  );
};

export default Checkout;